<template>
  <vb-dropdown
    ref="dropdown"
    class="dropdown multichoice-dropdown"
    :class="{ 'button-select': !isBtnGroup }"
    :close-on-item-click="singleChoice"
    :is-btn-group="isBtnGroup"
    @open="onDropdownOpen()"
  >
    <button
      v-show="isBtnGroup"
      slot="button"
      type="button"
      class="btn btn-sm btn-secondary btn-transparent"
      data-toggle="dropdown"
    >
      {{ mainButtonLabel }}
    </button>
    <button
      v-show="isBtnGroup"
      slot="button"
      type="button"
      data-toggle="dropdown"
      aria-haspopup="true"
      aria-expanded="false"
      class="btn btn-sm btn-secondary btn-transparent dropdown-toggle"
    >
      <span class="caret"></span>
      <span class="sr-only">Toggle Dropdown</span>
    </button>
    <button
      v-show="!isBtnGroup"
      slot="button"
      class="btn btn-default btn-block dropdown-toggle"
      data-toggle="dropdown"
      aria-haspopup="true"
      aria-expanded="false"
    >
      {{ mainLabel }}
      <i class="ion-chevron-down"></i>
    </button>
    <slot>
      <li v-if="searchEnabled">
        <span class="input-search">
          <input
            ref="searchInput"
            v-model="search"
            type="text"
            placeholder="Search..."
            class="form-control"
            @keyup.up="movePositionUp()"
            @keyup.down="movePositionDown()"
            @keyup.enter="selectAtPosition()"
            @input="pos = 0"
          />
        </span>
      </li>
      <li v-show="showAll" @click.prevent="selectAll()">
        <a slot="selectAllLabel" href>All</a>
      </li>
      <li v-show="showAll" role="separator" class="divider"></li>

      <li
        v-for="(choice, idx) in visibleChoices"
        :class="{ active: isSelected(choice) }"
        @click.prevent="toggle(choice)"
      >
        <a href :class="{ focused: pos === idx && showActivePosition }">
          <el-popover
            title="Tip"
            placement="left"
            trigger="hover"
            :disabled="!choice.help"
            width="400"
          >
            <div v-html="choice.help"></div>
            <span slot="reference" class="choice-label">
              {{ choice.label }}
            </span>
          </el-popover>
        </a>
      </li>

      <li v-show="showClearAll" role="separator" class="divider"></li>
      <li v-show="showClearAll" @click.prevent="clearAll()">
        <a slot="clearAllLabel" href>Clear all</a>
      </li>
    </slot>
  </vb-dropdown>
</template>

<script>
import Vue from 'vue'
import _ from 'lodash'
import HelpTip from 'components/common/HelpTip'

export default {
  components: { HelpTip },
  props: {
    title: { type: String },
    label: { type: String },
    showAll: { type: Boolean, default: false },
    showClearAll: { type: Boolean, default: true },
    choices: { type: Array },
    singleChoice: { type: Boolean, default: false },
    searchEnabled: { type: Boolean, default: false },
    value: {},
    isBtnGroup: { type: Boolean, default: false },
  },
  data: () => {
    return {
      selected: [],
      noop: () => {
        console.log(' => MultipleChoicesDropdown.clickOutsideCallback() [noop]')
      },
      search: '',
      pos: -1,
      showActivePosition: false,
    }
  },
  computed: {
    mainLabel: function () {
      if (this.selected.length) {
        return this.selected.map((c) => c.label).join(', ')
      } else {
        return `Select ${this.title}`
      }
    },
    mainButtonLabel() {
      if (this.selected.length)
        return this.selected.map((c) => c.label).join(', ')

      return this.title
    },
    visibleChoices: function () {
      if (this.search) {
        const search = _.escapeRegExp(this.search)
        const re = new RegExp(search, ['i'])
        return this.choices.filter((c) => c.label.search(re) >= 0)
      } else {
        return this.choices
      }
    },
  },
  watch: {
    value(val) {
      this.updateValue(val)
    },
  },
  created() {
    this.updateValue(this.value)
  },
  methods: {
    onDropdownOpen() {
      self = this
      self.pos = -1
      self.showActivePosition = false // should be shown after first up/down key stroke
      if (this.searchEnabled) {
        self.$nextTick(() => {
          self.$refs.searchInput.focus()
          self.search = ''
        })
      }
    },
    isSelected(choice) {
      return new Set(this.selected).has(choice)
    },
    toggle(choice, opts) {
      opts = opts || { emitEvents: true }
      if (this.singleChoice) {
        this.deselectAll()
        choice.selected = true
      } else {
        choice.selected = !choice.selected
      }
      this.updateSelected({ emitEvents: opts.emitEvents })
    },
    clearAll() {
      this.deselectAll()
      this.updateSelected()
    },
    deselectAll() {
      this.choices.forEach((c) => {
        c.selected = false
      })
    },
    selectAll() {
      const allSelected = this.choices.every((c) => c.selected)
      if (allSelected) {
        this.choices.forEach((c) => {
          c.selected = false
        })
      } else {
        this.choices.forEach((c) => {
          c.selected = true
        })
      }
      this.updateSelected()
    },
    updateSelected(opts) {
      opts = opts || { emitEvents: true }
      this.selected = this.choices.filter((c) => c.selected)
      if (opts.emitEvents) {
        this.emitEvents()
      }
    },
    emitEvents() {
      if (this.singleChoice) {
        this.$emit('change', this.selected[0])
        this.$emit('input', this.selected[0].value)
      } else {
        this.$emit('change', this.selected)
        this.$emit('input', this.selected.value)
      }
    },
    updateValue(val) {
      const getChoiceByValue = (val) =>
        this.choices.filter((c) => c.value === val)[0]
      if (val) {
        if (_.isArray(val)) {
          if (this.singleChoice) {
            throw new Error(
              'Dropdown is configured to use singleChoice but value is an array',
              val,
            )
          }
          this.deselectAll()
          val.forEach((v) => {
            const choice = getChoiceByValue(v)
            choice.selected = true
          })
          this.updateSelected({ emitEvents: false })
        } else {
          const choice = getChoiceByValue(val)
          if (choice) {
            this.toggle(choice, { emitEvents: false })
          } else {
            const choices = this.choices.map((c) => c.value)
            const msg = ` => value was set to ${val} but could not find a choice for it (choices: ${choices})`
            console.warn(msg)
          }
        }
      }
    },
    movePositionDown() {
      this.showActivePosition = true
      if (this.pos < this.visibleChoices.length - 1) {
        this.pos += 1
      } else {
        this.pos = 0
        this.scrollToTop()
      }
    },
    movePositionUp() {
      this.showActivePosition = true
      if (this.pos > 0) {
        this.pos -= 1
      } else {
        this.pos = this.visibleChoices.length - 1
        this.scrollToBottom()
      }
    },
    scrollToTop() {
      const $dropdown = this.$el.querySelector('.dropdown-menu')
      $dropdown.scrollTop = 0
    },
    scrollToBottom() {
      const $dropdown = this.$el.querySelector('.dropdown-menu')
      $dropdown.scrollTop = $dropdown.scrollHeight
    },
    selectAtPosition() {
      if (this.pos < 0 || this.pos >= this.visibleChoices.length) {
        return
      }
      const choice = this.visibleChoices[this.pos]
      this.toggle(choice)
      this.$refs.dropdown.show = false
    },
  },
}
</script>

<style lang="scss">
.multichoice-dropdown .dropdown-menu {
  li a,
  li a:hover {
    padding: 0;

    .choice-label {
      padding: 8px 6px;
      display: inline-block;
      width: 100%;
    }
  }
}
</style>
